// Stufenbranches: Board und Aktionen der Transportfreigabe (Vertrag 14, 16.2).
// Der Hook liest GET /api/cicd/transport/stage-board und reicht die Adapter-Ausgabe an PromotionStageBoard weiter.
// Zustände, Kandidaten-IDs und Nachweise kommen ausschließlich vom Server; der Hook leitet nichts davon ab.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiHeaders, handleUnauthorized } from "../utils/helpers";
import useCicdEvents from "../hooks/useCicdEvents";
import { mergeRequests, mergePreparations, withoutArchivedPreparations, withRequestProgress } from "./progressState";
import { projectRegion, regionsOf, toStageBoardProps } from "./boardAdapter";

export function packageDetailSignature(board, packageId, sourceStage) {
  const pkg = board?.packages?.find(p => p.packageId === packageId);
  return JSON.stringify([sourceStage || null, sourceStage ? pkg?.cells?.[sourceStage] : pkg?.cells,
    board?.stages?.map(({ key, env, instanceId, targets }) => ({ key, env, instanceId, targets })),
    board?.requests?.filter(r => r.items?.some(i => i.packageId === packageId) || r.packages?.some(p => p.packageId === packageId))]);
}

const BASE = "/api/cicd/transport";
const CONFIG = "/api/cicd/stage-branches/config";
// Schreibaktionen und kleine Leseaufrufe wie api(): Abbruch nach 30 s, nie eine automatische Wiederholung.
const TIMEOUT_MS = 30000;
// Board und Paketdetails (Ladezeit, Maßnahme 5a): Ein kalter Aufbau liest bis zu 69 GitHub-Objekte nacheinander und braucht
// 25 bis 60 s. Deshalb 90 s und genau eine Wiederholung nach kurzer Pause bei Zeitüberschreitung, Netzfehler, 408, 429 und
// 5xx. Bestätigte serverseitige Fristen bei Paketdetails werden nicht automatisch wiederholt. Nie bei anderen 4xx.
export const READ_TIMEOUT_MS = 90000;
// Schreibaktionen (Antrag, Freigabe, Ablehnen, Wiederholen): Der Server prüft vor dem Schreiben frisch gegen Git und braucht dafür
// bei großen Spaces und nach einem Neustart deutlich länger als 30 s. Der Load Balancer lässt 300 s zu.
export const ACTION_TIMEOUT_MS = 120000;
// So lange liest das Board nach einer Zeitüberschreitung einer Schreibaktion regelmäßig nach.
export const ACTION_WATCH_MS = 10 * 60000;
export const READ_RETRY_DELAY_MS = 2000;
// Längstes Lesefenster eines Board-Abrufs (zwei Versuche plus Pause). Overlays der Seite verfallen frühestens danach.
export const BOARD_READ_WINDOW_MS = READ_TIMEOUT_MS * 2 + READ_RETRY_DELAY_MS;
const RETRY_STATUS = new Set([408, 429]);
// Längstes Warten einer Schreibaktion auf den anschließenden Board-Abruf (wie die bisherige Lesefrist).
export const ACTION_REFRESH_WAIT_MS = 30000;
// Eigene Codes des Browsers ohne Serverstatus; ihre Texte stehen in stageBranches.errors (freigabe.json).
const READ_TIMEOUT = "STAGE_READ_TIMEOUT", ACTION_TIMEOUT = "STAGE_ACTION_TIMEOUT", CONNECTION_FAILED = "STAGE_CONNECTION_FAILED";
const localError = (code, details) => Object.assign(new Error(""), { code, status: null, ...(details ? { details } : {}) });
const ACTION_UNKNOWN = "STAGE_ACTION_OUTCOME_UNKNOWN";
const ACCEPTED_REQUEST = new Set(["queued", "running", "unknown", "completed", "partial", "failed"]);
const authIdentity = () => { try { return apiHeaders().Authorization || ""; } catch { return ""; } };
const boardRequestId = request => request.id && request.requestId && request.id !== request.requestId ? null : request.requestId || request.id;
const actionObserved = (action, request) => (action.clientRequestId && request.clientRequestId === action.clientRequestId)
  || (action.action === "approve" && action.wasPending && boardRequestId(request) === action.requestId
    && request.pinDigest === action.pinDigest && ACCEPTED_REQUEST.has(request.status));
const actionObservedPreparation = (action, preparation) => {
  const baseline = action.preparationVersions?.[preparation.id];
  if (baseline != null && !(preparation.revision > baseline)) return false;
  return (action.preparationId === preparation.id && preparation.revision > (baseline || 0))
    || (preparation.action === "request" && action.clientRequestId && preparation.clientRequestId === action.clientRequestId)
    || (preparation.action === "approve" && action.action === "approve" && action.wasPending
      && preparation.requestId === action.requestId && !!action.pinDigest && preparation.pinDigest === action.pinDigest);
};
// Solange Pakete laufen oder warten, liest der Hook das Board in diesem Abstand neu (nur sichtbarer Tab).
const POLL_MS = 15000;
const BOARD_RECOVERY_DELAYS_MS = [2000, 5000, 15000];
// Meldet der Server kurz „pending“ oder „unavailable“ (z. B. Schreibvorgang zwischen Hinweis und Lesen, H4), bleibt der zuletzt
// bestätigte Stand so lange als veraltet markiert sichtbar. Danach gilt wieder die Serverantwort ohne Board.
export const BOARD_HOLD_MS = 5 * 60000;
const boardRevisionPending = (wanted, current) => wanted != null && (Number.isSafeInteger(wanted) && Number.isSafeInteger(current)
  ? wanted > current : wanted !== current);
const ACTIVE_REQUEST = new Set(["queued", "running", "unknown"]);
// Ohne Board-Inhalt: Schalter aus oder kein Leserecht. Andere Lesefehler behalten den letzten Stand.
const CLEARS_BOARD = new Set(["STAGE_BRANCHES_DISABLED", "STAGE_REQUEST_FORBIDDEN"]);

const query = value => encodeURIComponent(value);
export const newRequestId = () => window.crypto.randomUUID?.()
  || Array.from(window.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");

// Eigener Abruf statt api(): Der Stufenpfad braucht code, message und details der Antwort unverändert (Vertrag 14, 16.2)
// und keine automatische Wiederholung; die Lesesteuerung des bisherigen Pfads über api() bleibt davon unberührt.
// Kopfzeilen, relative Pfade, Zeitlimit und 401 wie api(), aus denselben Helfern (apiHeaders, handleUnauthorized).
async function call(path, { method = "GET", body, signal, timeoutMs = TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  // Ein äußeres Signal (etwa Dialog geschlossen) bricht den Abruf ebenfalls ab.
  if (signal) { if (signal.aborted) controller.abort(); else signal.addEventListener("abort", () => controller.abort(), { once: true }); }
  // Ein nicht lesbarer Browserspeicher zählt wie ein fehlendes Token; die Antwort entscheidet (401 meldet ab).
  let headers;
  try { headers = apiHeaders(); } catch { headers = apiHeaders(null); }
  let response;
  try {
    response = await fetch(path, { method, cache: "no-store", headers, signal: controller.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  } catch (failure) {
    // Zeitüberschreitung und Netzfehler bekommen einen fachlichen Code statt des Rohtexts der DOMException oder des
    // TypeError („signal is aborted without reason“, „Failed to fetch“). Ein Abbruch durch den Aufrufer bleibt, wie er ist.
    if (timedOut) throw localError(method === "GET" ? READ_TIMEOUT : ACTION_TIMEOUT, { seconds: Math.round(timeoutMs / 1000) });
    if (controller.signal.aborted) throw failure;
    throw localError(CONNECTION_FAILED);
  } finally { clearTimeout(timer); }
  // Wie api(): abmelden, neu laden, kein Ergebnis.
  if (response.status === 401) { handleUnauthorized(); return undefined; }
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.error || `HTTP ${response.status}`);
    error.code = payload?.code || null;
    error.status = response.status;
    error.details = payload?.details;
    throw error;
  }
  return payload;
}

// Pilot: Die Frist umfasst Header und Body. Ein Browserabbruch beendet nur den HTTP-Aufruf.
export async function pilotCall(path, { method = "GET", body, signal, timeoutMs = TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  let expired = false, timer, abort;
  const cancelled = new Promise((_, reject) => {
    abort = () => { controller.abort(); reject(new DOMException("Aborted", "AbortError")); };
    if (signal?.aborted) abort(); else signal?.addEventListener("abort", abort, { once: true });
    timer = setTimeout(() => { expired = true; abort(); }, timeoutMs);
  });
  try {
    return await Promise.race([cancelled, (async () => {
      if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
      let headers;
      try { headers = apiHeaders(); } catch { headers = apiHeaders(null); }
      const response = await fetch(path, { method, cache: "no-store", headers, signal: controller.signal,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
      if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
      if (response.status === 401) {
        handleUnauthorized();
        throw Object.assign(new Error(""), { status: 401, code: "STAGE_REQUEST_FORBIDDEN" });
      }
      let payload;
      try { payload = await response.json(); }
      catch (error) {
        if (response.ok || controller.signal.aborted) throw error;
        payload = null;
      }
      if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");
      if (!response.ok) {
        throw Object.assign(new Error(payload?.error || `HTTP ${response.status}`), {
          code: payload?.code || null, status: response.status, details: payload?.details, outcome: payload?.outcome });
      }
      return payload;
    })()]);
  } catch (error) {
    if (expired) throw localError(method === "GET" ? READ_TIMEOUT : ACTION_TIMEOUT, { seconds: Math.round(timeoutMs / 1000) });
    if (signal?.aborted || error?.status) throw error;
    throw localError(CONNECTION_FAILED);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

// Lesender Board-Abruf (Board, Paketdetails) mit genau einer Wiederholung. Nur hier, nie für Schreibaktionen: Ein POST
// könnte beim zweiten Mal doppelt wirken, ein GET nicht. `stillWanted()` lässt die Wiederholung aus, wenn inzwischen ein
// neuerer Abruf läuft oder die Komponente weg ist; ein äußeres Abbruchsignal ebenso.
const retryableRead = error => (error?.status == null ? [READ_TIMEOUT, CONNECTION_FAILED].includes(error?.code)
  : RETRY_STATUS.has(error.status) || error.status >= 500);
const pause = ms => new Promise(resolve => setTimeout(resolve, ms));
async function readBoardPath(path, { signal, stillWanted = () => true, request = call, timeoutMs = READ_TIMEOUT_MS } = {}) {
  try { return await request(path, { signal, timeoutMs }); }
  catch (failure) {
    if (!retryableRead(failure) || signal?.aborted || !stillWanted()
      || (path.includes("/stage-board/package?") && failure.code === "STAGE_BOARD_TIMEOUT")) throw failure;
    await pause(READ_RETRY_DELAY_MS);
    if (signal?.aborted || !stillWanted()) throw failure;
    return request(path, { signal, timeoutMs });
  }
}

const asError = error => ({ code: error?.code || null, message: error?.message || "", status: error?.status ?? null,
  ...(error?.details === undefined ? {} : { details: error.details }) });

const disabledError = () => Object.assign(new Error("STAGE_BRANCHES_DISABLED"), { code: "STAGE_BRANCHES_DISABLED", status: null });

const validBoard = board => !!board && typeof board === "object" && Array.isArray(board.stages) && Array.isArray(board.packages);

// Laufende oder wartende Arbeit der Beförderung: dann liest der Hook nach.
const boardBusy = board => !!board && ((board.capacity?.running || 0) > 0 || (board.capacity?.waiting || 0) > 0
  || board.packages.some(pkg => Object.values(pkg.cells || {}).some(cell => cell?.state === "running")
    || (pkg.openRequests || []).some(request => ACTIVE_REQUEST.has(request.status))));

// Zuletzt bestätigte eingeschaltete Spaces dieser Sitzung: Die Seite öffnet sie sofort mit dem Stufen-Board und prüft
// den Schalter dabei neu. Ausgeschaltete Spaces werden nicht gemerkt, sie behalten den heutigen Ablauf ohne Umweg.
const enabledSpaces = new Map();

/**
 * Schalter des Space aus `GET /api/cicd/stage-branches/config` (Vertrag 14, 16.2).
 * `enabled` ist nur bei `stageBranches.enabled === true` wahr, bei jedem Fehler `false` mit `error.code`.
 */
export function useStageBranchesConfig(spaceId) {
  const [state, setState] = useState({ spaceId: null, config: null, error: null, loading: false });
  // reload() liest den Schalter erneut, etwa nachdem der Server einen bisherigen Weg mit STAGE_BRANCHES_ACTIVE abgelehnt hat.
  const [generation, setGeneration] = useState(0);
  const reload = useCallback(() => setGeneration(value => value + 1), []);
  useEffect(() => {
    if (!spaceId) { setState({ spaceId, config: null, error: null, loading: false }); return undefined; }
    let live = true;
    setState({ spaceId, config: enabledSpaces.get(spaceId) || null, error: null, loading: true });
    call(`${CONFIG}?spaceId=${query(spaceId)}`)
      .then(result => {
        const config = result && typeof result === "object" ? result : null;
        if (config?.stageBranches?.enabled === true) enabledSpaces.set(spaceId, config); else enabledSpaces.delete(spaceId);
        if (live) setState({ spaceId, config, error: null, loading: false });
      })
      .catch(error => {
        enabledSpaces.delete(spaceId);
        if (live) setState({ spaceId, config: null, error: asError(error), loading: false });
      });
    return () => { live = false; };
  }, [spaceId, generation]);
  const current = state.spaceId === spaceId;
  const config = current ? state.config : null;
  return { enabled: config?.stageBranches?.enabled === true, config, loading: current ? state.loading : !!spaceId,
    error: current ? state.error : null, reload };
}

/**
 * Board und Aktionen eines Space (Vertrag 16.2). Mit `enabled: false` gibt es keinen Aufruf an `stage-*` oder `prod-lock`.
 * Fehler stehen in `error` als `{ code, message }`; Aktionen werfen sie zusätzlich, `refresh` nicht.
 */
export function useStageBoard(spaceId, { topicId, enabled = false, includeDev = false, region = null, readModelEnabled = false, readModel = null } = {}) {
  const { t, i18n } = useTranslation("freigabe");
  const active = enabled === true && !!spaceId;
  const identity = readModelEnabled ? authIdentity() : "";
  const localReads = readModelEnabled && readModel?.localReads === true;
  const bindingKey = localReads ? readModel.bindingKey : null;
  const generation = localReads ? readModel.generation : null;
  const scopeKey = JSON.stringify([spaceId, active, readModelEnabled, identity, localReads, bindingKey, generation]);
  const scope = useRef(null);
  if (scope.current?.key !== scopeKey) scope.current = { key: scopeKey, identity, period: 0, controllers: new Set() };
  const ownedScope = scope.current;
  const scopeCurrent = useCallback((period = ownedScope.period) => scope.current === ownedScope && period === ownedScope.period
    && (!readModelEnabled || authIdentity() === ownedScope.identity), [ownedScope, readModelEnabled]);
  const scopedCall = useCallback(async (path, options = {}) => {
    if (!readModelEnabled) return call(path, options);
    if (!scopeCurrent()) throw new DOMException("Aborted", "AbortError");
    const period = ownedScope.period;
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (options.signal?.aborted) abort(); else options.signal?.addEventListener("abort", abort, { once: true });
    ownedScope.controllers.add(controller);
    try {
      const result = await pilotCall(path, { ...options, signal: controller.signal });
      if (!scopeCurrent(period)) throw new DOMException("Aborted", "AbortError");
      return result;
    }
    finally { ownedScope.controllers.delete(controller); options.signal?.removeEventListener("abort", abort); }
  }, [readModelEnabled, scopeCurrent, ownedScope]);
  useEffect(() => () => { for (const controller of ownedScope.controllers) controller.abort(); }, [ownedScope]);
  // requestedAt: Uhrzeit des Browsers, zu der der Abruf des gezeigten Boards begann. Die Seite löst Overlays nur mit einem
  // Board auf, das nach der Aktion angefordert wurde (I5); Browseruhr statt generatedAt des Servers, damit kein Uhrversatz
  // zwischen Browser und Server die Auflösung verfälscht.
  const [state, setState] = useState({ board: null, requestedAt: null, loading: active, readError: null });
  const [dataScope, setDataScope] = useState(scopeKey);
  const [actionError, setActionError] = useState(null);
  const [uncertainActions, setUncertainActions] = useState([]);
  const [progress, setProgress] = useState({ requests: [], preparations: [], error: null });
  const progressRead = useRef(null);
  const progressMutation = useRef(0);
  const progressSnapshot = useRef(null);
  const boardRecovery = useRef(null);
  const currentBoardRevision = useRef(null);
  currentBoardRevision.current = state.readModel?.revision ?? null;
  // Paketdetails auf Anfrage (Vertrag 27.3), je Paket einmal geladen; das Board selbst lädt sie nie mit.
  const [details, setDetails] = useState({});
  const detailCache = useRef(new Map());
  const detailBoard = useRef(null);
  detailBoard.current = state.board;
  const shownState = useRef(state);
  shownState.current = state;
  // Browserzeit, seit der ein bestätigter Stand trotz „pending“/„unavailable“ gehalten wird (H4); null ohne Halten.
  const boardHold = useRef(null);
  const alive = useRef(true);
  const version = useRef(0);
  // Version des neuesten Abrufs, solange er läuft; ältere, durch einen neueren ersetzte Abrufe zählen nicht mehr.
  const reading = useRef(null);
  // Browserzeit der letzten vom Server angenommenen Schreibaktion. Die Seite löst Overlays nur mit einem Board auf, dessen
  // Abruf danach begann (I5): ein Poll, der während des laufenden POST startete, trägt noch den Stand von davor.
  const [lastAcceptedAt, setLastAcceptedAt] = useState(null);
  const translate = useRef(t);
  translate.current = t;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // refresh({ whenIdle: true }) für Polling: kein weiterer Abruf, solange der neueste noch läuft. Mit 90 s Lesefrist
  // würden sich die Abrufe sonst stapeln und jeder einen eigenen Aufbau auf dem Server auslösen.
  const refresh = useCallback(async options => {
    if (!active) return null;
    if (options?.whenIdle === true && reading.current !== null && reading.current === version.current) {
      if (localReads && options.afterCurrent === true) ownedScope.refreshPending = true;
      return null;
    }
    ownedScope.refreshPending = false;
    const current = ++version.current;
    const period = ownedScope.period;
    const requestedAt = Date.now();
    reading.current = current;
    setState(previous => ({ ...previous, loading: true }));
    try {
      // Recovery owns its finite retry budget; do not nest the ordinary read retry.
      const read = options?.recovery === true ? (path, settings) => scopedCall(path, settings) : readBoardPath;
      const board = await read(`${BASE}/stage-board?spaceId=${query(spaceId)}`,
        { request: scopedCall, timeoutMs: localReads ? 10000 : READ_TIMEOUT_MS, stillWanted: () => alive.current && scopeCurrent(period) && current === version.current });
      const metadata = localReads ? board?.readModel : null;
      if (localReads && (!metadata || metadata.bindingKey !== bindingKey || metadata.generation !== generation))
        throw new Error(translate.current("promotion.invalidResponse"));
      const pending = localReads && ["pending", "unavailable"].includes(metadata?.state);
      if (!pending && !validBoard(board)) throw new Error(translate.current("promotion.invalidResponse"));
      if (alive.current && scopeCurrent(period) && current === version.current) {
        // A replica may finish an older cached read after the latest SSE revision.
        if (localReads && !pending && Number.isSafeInteger(metadata?.revision) && Number.isSafeInteger(currentBoardRevision.current)
            && metadata.revision < currentBoardRevision.current) {
          setState(previous => ({ ...previous, loading: false }));
          return null;
        }
        // Ein vorübergehend fehlender Stand verwirft das bestätigte Board nicht (H4): Board, Revision und Abrufzeit bleiben, die
        // Aktualität zeigt „veraltet“. Schreibaktionen prüft der Server ohnehin frisch (pinDigest, candidateId).
        const shown = shownState.current;
        if (!pending) boardHold.current = null;
        else if (validBoard(shown.board) && shown.readModel && (boardHold.current === null || Date.now() - boardHold.current < BOARD_HOLD_MS)) {
          boardHold.current ??= Date.now();
          const wantedRevision = progressSnapshot.current?.boardRevision;
          if (wantedRevision != null && boardRevisionPending(wantedRevision, shown.readModel.revision) && ownedScope.requestedBoardRevision !== wantedRevision) {
            ownedScope.requestedBoardRevision = wantedRevision;
            ownedScope.refreshPending = true;
          }
          setState(previous => ({ ...previous, loading: false, readError: null, readModel: previous.readModel && { ...previous.readModel,
            state: "stale", stale: true, held: true, refreshing: metadata.refreshing === true, error: metadata.error || null } }));
          return board;
        }
        currentBoardRevision.current = metadata?.revision ?? null;
        const wantedRevision = progressSnapshot.current?.boardRevision;
        if (localReads && wantedRevision != null && boardRevisionPending(wantedRevision, metadata?.revision) && ownedScope.requestedBoardRevision !== wantedRevision) {
          ownedScope.requestedBoardRevision = wantedRevision;
          ownedScope.refreshPending = true;
        }
        setState({ board: pending ? null : board, readModel: metadata, requestedAt, loading: false, readError: null });
        if (readModelEnabled) setUncertainActions(previous => previous.filter(action => !(board.requests || []).some(request => actionObserved(action, request))));
      }
      return board;
    } catch (failure) {
      const error = asError(failure);
      ownedScope.requestedBoardRevision = null;
      // Der bisher geladene Stand bleibt sichtbar; die Seite zeigt den Lesefehler nur als Hinweis daneben.
      if (alive.current && scopeCurrent(period) && current === version.current) {
        const accessLost = readModelEnabled && [401, 403].includes(error.status);
        setState(previous => ({ board: CLEARS_BOARD.has(error.code) || accessLost ? null : previous.board,
          readModel: accessLost ? null : previous.readModel, requestedAt: previous.requestedAt, loading: false, readError: error }));
        if (accessLost) {
          setProgress({ requests: [], preparations: [], error: null });
          progressSnapshot.current = null;
          // Alle Antworten der bisherigen Zugriffsperiode verwerfen, auch bereits gestartete Detail- und Schreibaufrufe.
          ownedScope.period += 1;
          for (const controller of ownedScope.controllers) controller.abort();
          setDetails({}); detailCache.current.clear();
          setLastAcceptedAt(null); setActionError(null);
          setUncertainActions([]); setWatchUntil(null);
        }
      }
      return null;
    } finally {
      if (reading.current === current) {
        reading.current = null;
        if (ownedScope.refreshPending) {
          ownedScope.refreshPending = false;
          // Coalesce events received during this read into one later read. A
          // completed response may predate the event that arrived while waiting.
          Promise.resolve().then(() => {
            if (alive.current && scopeCurrent(period)) refresh({ whenIdle: true });
          });
        }
      }
    }
  }, [active, spaceId, scopedCall, scopeCurrent, readModelEnabled, ownedScope, localReads, bindingKey, generation]);

  useEffect(() => {
    version.current += 1;
    setDataScope(scopeKey);
    setState({ board: null, requestedAt: null, loading: active, readError: null });
    // Ein Stand des vorherigen Bereichs wird nie gehalten, auch wenn die erste Antwort vor dem nächsten Rendern eintrifft.
    shownState.current = { board: null, readModel: null }; boardHold.current = null;
    setActionError(null);
    setUncertainActions([]);
    setProgress({ requests: [], preparations: [], error: null });
    progressSnapshot.current = null;
    setLastAcceptedAt(null);
    setWatchUntil(null);
    setDetails({}); detailCache.current.clear();
    if (active) refresh();
  }, [active, refresh, scopeKey]);

  // Nach einer Schreibaktion ohne Antwort in der Frist (läuft meist auf dem Server weiter) liest der Hook eine Weile nach,
  // auch wenn das Board noch nichts Laufendes zeigt (Antrag noch „pending“): der Hinweis „aktualisiert sich automatisch“ gilt.
  // Ende des Beobachtungsfensters als Zeitpunkt: ein weiterer Timeout verlängert es, ein Spacewechsel setzt es zurück.
  const [watchUntil, setWatchUntil] = useState(null);
  useEffect(() => {
    if (watchUntil === null) return undefined;
    const timer = setTimeout(() => setWatchUntil(null), Math.max(0, watchUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [watchUntil]);
  // Status reads use local receipts only. A phase change must not rebuild the package board.
  const refreshProgress = useCallback(async () => {
    if (!active || !localReads || !scopeCurrent()) return null;
    if (progressRead.current?.scope === ownedScope) {
      progressRead.current.pending = true;
      return progressRead.current.promise;
    }
    const operation = { scope: ownedScope, pending: false, promise: null };
    const period = ownedScope.period;
    progressRead.current = operation;
    operation.promise = (async () => {
      do {
        operation.pending = false;
        const mutationVersion = progressMutation.current;
        try {
          const result = await scopedCall(`${BASE}/stage-progress?spaceId=${query(spaceId)}`, { timeoutMs: 10000 });
          if (!alive.current || !scopeCurrent(period)) return null;
          if (!result || (result.requestedSpaceId || result.spaceId) !== spaceId || result.bindingKey !== bindingKey || result.generation !== generation
            || !Array.isArray(result.requests) || !Array.isArray(result.preparations)) throw new Error(translate.current("promotion.invalidResponse"));
          if (mutationVersion !== progressMutation.current) { operation.pending = true; continue; }
          const previousSnapshot = progressSnapshot.current;
          const changedPreparationBinding = previousSnapshot?.preparationBinding && result.preparationBinding
            && previousSnapshot.preparationBinding !== result.preparationBinding;
          progressSnapshot.current = { ...result, boardRevision: Number.isSafeInteger(previousSnapshot?.boardRevision)
            && Number.isSafeInteger(result.boardRevision) ? Math.max(previousSnapshot.boardRevision, result.boardRevision) : result.boardRevision };
          const ids = new Set(result.requests.map(row => row.requestId || row.id));
          setProgress(previous => ({ requests: mergeRequests(result.complete ? previous.requests.filter(row => ids.has(row.requestId || row.id)) : previous.requests, result.requests),
            // Absence from a replica's cached list is not a revocation of an accepted preparation.
            // A different preparation binding does revoke the former configuration's receipts.
            preparations: withoutArchivedPreparations(mergePreparations(changedPreparationBinding ? [] : previous.preparations, result.preparations),
              result.preparations, result.complete === true),
            complete: result.complete === true, error: null }));
          setUncertainActions(previous => previous.filter(action => !result.requests.some(request => actionObserved(action, request))
            && !result.preparations.some(preparation => actionObservedPreparation(action, preparation))));
          if (result.boardRevision != null && (currentBoardRevision.current != null || reading.current === null)
            && boardRevisionPending(result.boardRevision, currentBoardRevision.current)
            && ownedScope.requestedBoardRevision !== result.boardRevision) {
            ownedScope.requestedBoardRevision = result.boardRevision;
            refresh({ whenIdle: true, afterCurrent: true });
          }
        } catch (failure) {
          if (!alive.current || !scopeCurrent(period)) return null;
          if ([401, 403].includes(failure?.status)) {
            setProgress({ requests: [], preparations: [], error: asError(failure) });
            ownedScope.period += 1; version.current += 1;
            for (const controller of ownedScope.controllers) controller.abort();
            setState({ board: null, readModel: null, requestedAt: null, loading: false, readError: asError(failure) });
            setDetails({}); detailCache.current.clear();
            setLastAcceptedAt(null); setActionError(null); setUncertainActions([]); setWatchUntil(null);
          } else setProgress(previous => ({ ...previous, error: asError(failure) }));
        }
      } while (operation.pending && alive.current && scopeCurrent(period));
      return progressSnapshot.current;
    })().finally(() => { if (progressRead.current === operation) progressRead.current = null; });
    return operation.promise;
  }, [active, localReads, scopeCurrent, ownedScope, scopedCall, spaceId, bindingKey, generation, refresh]);
  useEffect(() => { if (active && localReads) refreshProgress(); }, [active, localReads, refreshProgress]);
  const connection = useCicdEvents(spaceId, refreshProgress, active && localReads,
    { localRevision: localReads, reconcileOnConnect: localReads });
  useEffect(() => {
    if (!localReads || connection !== "forbidden") return;
    ownedScope.period += 1;
    version.current += 1;
    for (const controller of ownedScope.controllers) controller.abort();
    setState({ board: null, readModel: null, requestedAt: null, loading: false,
      readError: { code: "STAGE_REQUEST_FORBIDDEN", status: 403, message: "" } });
    setDetails({}); detailCache.current.clear();
    setLastAcceptedAt(null); setActionError(null); setUncertainActions([]); setWatchUntil(null);
    setProgress({ requests: [], preparations: [], error: null });
  }, [localReads, connection, ownedScope]);
  const pendingProjection = localReads && (["pending", "unavailable"].includes(state.readModel?.state) || state.readModel?.held === true);
  const wantedBoardRevision = progressSnapshot.current?.boardRevision ?? null;
  const boardAccessLost = connection === "forbidden" || [401, 403].includes(state.readError?.status)
    || [401, 403].includes(progress.error?.status) || CLEARS_BOARD.has(state.readError?.code);
  useEffect(() => {
    if (!active || !localReads || boardAccessLost || !scopeCurrent()) {
      boardRecovery.current = null;
      return undefined;
    }
    const needsRecovery = !!state.readError || state.readModel?.held === true || boardRevisionPending(wantedBoardRevision, state.readModel?.revision);
    if (!needsRecovery) { boardRecovery.current = null; return undefined; }
    if (boardRecovery.current?.scope !== ownedScope || boardRecovery.current.revision !== wantedBoardRevision)
      boardRecovery.current = { scope: ownedScope, revision: wantedBoardRevision, attempts: 0, nextAt: null };
    const recovery = boardRecovery.current, period = ownedScope.period;
    if (state.loading || recovery.attempts >= BOARD_RECOVERY_DELAYS_MS.length) return undefined;
    if (recovery.nextAt === null) recovery.nextAt = Date.now() + BOARD_RECOVERY_DELAYS_MS[recovery.attempts];
    let timer;
    const schedule = () => {
      clearTimeout(timer);
      if (document.visibilityState === "hidden" || !scopeCurrent(period)) return;
      timer = setTimeout(() => {
        if (document.visibilityState === "hidden" || !scopeCurrent(period) || boardRecovery.current !== recovery) return;
        recovery.attempts += 1; recovery.nextAt = null;
        void refresh({ whenIdle: true, recovery: true });
      }, Math.max(0, recovery.nextAt - Date.now()));
    };
    schedule();
    document.addEventListener("visibilitychange", schedule);
    return () => { clearTimeout(timer); document.removeEventListener("visibilitychange", schedule); };
  }, [active, localReads, boardAccessLost, scopeCurrent, ownedScope, state.loading, state.readError,
    state.readModel?.revision, state.readModel?.held, state.requestedAt, wantedBoardRevision, refresh]);
  // Ein gehaltenes Board läuft spätestens nach BOARD_HOLD_MS ab, auch ohne neues Ereignis und nach erschöpfter Recovery
  // (Abnahme DEV252, D). Ein einzelner Abruf entscheidet dann: Fehlt der Stand weiterhin, leert die Antwort das Board.
  // Je Haltephase genau ein Ablaufabruf: ein erneutes Rendern während dieses Abrufs startet keinen zweiten, der ihn überholt.
  const holdExpired = useRef(null);
  useEffect(() => {
    const since = boardHold.current;
    if (!active || !localReads || state.readModel?.held !== true || since === null || holdExpired.current === since) return undefined;
    const period = ownedScope.period;
    const timer = setTimeout(() => {
      if (!scopeCurrent(period) || boardHold.current !== since) return;
      holdExpired.current = since;
      void refresh({ recovery: true });
    }, Math.max(0, since + BOARD_HOLD_MS - Date.now()));
    return () => clearTimeout(timer);
  }, [active, localReads, state.readModel?.held, ownedScope, scopeCurrent, refresh]);
  const busy = pendingProjection || progress.preparations.some(row => row.status === "preparing") || progress.requests.some(row => ["queued", "running", "unknown"].includes(row.status)) || boardBusy(state.board) || watchUntil !== null || (readModelEnabled && uncertainActions.length > 0);
  const pollMs = readModelEnabled && uncertainActions.length > 0 && watchUntil === null && !boardBusy(state.board) ? 60000 : POLL_MS;
  useEffect(() => {
    if (readModelEnabled && uncertainActions.length === 0) {
      setActionError(previous => previous?.code === ACTION_UNKNOWN ? null : previous);
      setWatchUntil(null);
    }
  }, [readModelEnabled, uncertainActions]);
  useEffect(() => {
    if (!active || boardAccessLost || (!busy && !progress.error)) return undefined;
    // Connected SSE delivers phase changes. Keep polling only for unresolved
    // reads/actions or an unavailable stream; the shared cursor still catches gaps.
    if (localReads && connection === "connected" && !progress.error && uncertainActions.length === 0) return undefined;
    const timer = setInterval(() => { if (document.visibilityState !== "hidden") {
      if (localReads) refreshProgress(); else refresh({ whenIdle: true });
    } }, pollMs);
    return () => clearInterval(timer);
  }, [active, busy, refresh, refreshProgress, localReads, pollMs, connection, progress.error, uncertainActions.length, boardAccessLost]);

  // Nach der Serverantwort startet der Board-Abruf sofort (I1: er beginnt nach der Aktion). Die Aktion wartet höchstens
  // ACTION_REFRESH_WAIT_MS darauf, damit Dialoge nicht bis zum Ende des Lesefensters (bis 182 s) offen bleiben; der Abruf
  // läuft weiter, und die neueste Version gewinnt (Versionszähler in refresh).
  // onAccepted (optional): Zeitpunkt der Serverantwort genau dieser Aktion, z. B. für das Gate eines Overlays (I5).
  const send = useCallback(async (path, method, body, { onAccepted } = {}) => {
    if (!active) throw disabledError();
    if (!scopeCurrent()) throw new DOMException("Aborted", "AbortError");
    const period = ownedScope.period;
    setActionError(null);
    let result;
    try { result = await scopedCall(path, { method, body: { spaceId, ...body }, timeoutMs: ACTION_TIMEOUT_MS }); }
    catch (failure) {
      if (!alive.current || !scopeCurrent(period)) throw failure;
      if (readModelEnabled && failure?.outcome !== "rejected"
        && ([ACTION_TIMEOUT, CONNECTION_FAILED].includes(failure?.code) || failure?.status >= 500 || failure?.status === 408)) {
        const match = path.match(/\/stage-requests\/([^/]+)\/(approve|reject|retry|refresh)$/);
        const pending = { key: body.requestId || path, path, method, requestId: match ? decodeURIComponent(match[1]) : null,
          action: match?.[2] || null,
          preparationId: path.match(/\/stage-preparations\/([^/]+)\/(retry|dismiss)$/)?.[1] || null,
          clientRequestId: path === `${BASE}/stage-requests` || match?.[2] === "refresh" ? body.requestId || null : null,
          pinDigest: body.pinDigest || null, preparationVersions: Object.fromEntries(progress.preparations.map(row => [row.id, row.revision])) };
        // Ein unverändert laufender Antrag belegt keine zweite Freigabe oder deren neue Notiz.
        pending.wasPending = match?.[2] === "approve" && (state.board?.requests || []).some(request =>
          boardRequestId(request) === pending.requestId && request.pinDigest === pending.pinDigest && request.status === "pending");
        setUncertainActions(previous => [...previous.filter(action => action.key !== pending.key), pending]);
        setWatchUntil(Date.now() + ACTION_WATCH_MS);
        const unknown = localError(ACTION_UNKNOWN, { causeCode: failure.code || null, ...(failure.details || {}) });
        unknown.outcomeUnknown = true;
        setActionError(asError(unknown));
        refresh();
        throw unknown;
      }
      setActionError(asError(failure));
      // Fachliche Antwort oder keine Antwort in der Frist: Die Aktion kann auf dem Server weiterlaufen; der neue Stand zeigt das Ergebnis.
      if (failure?.status || failure?.code === ACTION_TIMEOUT) refresh();
      if (failure?.code === ACTION_TIMEOUT && alive.current) setWatchUntil(Date.now() + ACTION_WATCH_MS);
      throw failure;
    }
    if (!alive.current || !scopeCurrent(period)) throw new DOMException("Aborted", "AbortError");
    const acceptedAt = Date.now();
    if (alive.current) setLastAcceptedAt(acceptedAt);
    if (typeof onAccepted === "function") onAccepted(acceptedAt);
    if (localReads && (result?.requestView || result?.preparation)) {
      progressMutation.current += 1;
      setProgress(previous => ({
      requests: result.requestView ? mergeRequests(previous.requests, [result.requestView]) : previous.requests,
      preparations: result.preparation ? mergePreparations(previous.preparations, [result.preparation]) : previous.preparations,
      complete: previous.complete, error: null,
    }));
    }
    const read = localReads && (result?.requestView || result?.preparation) ? refreshProgress() : refresh();
    // Im lokalen Lesemodell ist die bestätigte Mutation fertig; die Ansicht liest unabhängig nach.
    if (localReads) return result;
    let timer;
    await Promise.race([read, new Promise(resolve => { timer = setTimeout(resolve, ACTION_REFRESH_WAIT_MS); })]);
    clearTimeout(timer);
    if (readModelEnabled && (!alive.current || !scopeCurrent(period))) throw new DOMException("Aborted", "AbortError");
    return result;
  }, [active, spaceId, refresh, scopedCall, scopeCurrent, readModelEnabled, state.board, ownedScope, localReads, refreshProgress, progress.preparations]);

  const language = () => (i18n.language || "").startsWith("en") ? "en" : "de";
  const request = (input = {}) => {
    const { requestId, sourceStage, targetStage, packageIds, note, topicId: requestTopic, release, targets } = input;
    return send(`${BASE}/stage-requests`, "POST", { requestId: requestId || newRequestId(), sourceStage, targetStage,
      packageIds, note, language: language(), ...(requestTopic ? { topicId: requestTopic } : {}), ...(release ? { release } : {}),
      // Nur im Mehrzielmodus mit gewählten Zielen (Vertrag V.4); sonst bleibt der Antrag byte gleich.
      ...(Array.isArray(targets) && targets.length ? { targets } : {}) });
  };
  const approve = (id, pinDigest, note) => send(`${BASE}/stage-requests/${query(id)}/approve`, "POST",
    { pinDigest, ...(typeof note === "string" && note.trim() ? { approvalNote: note.trim() } : {}) });
  const reject = (id, reason) => send(`${BASE}/stage-requests/${query(id)}/reject`, "POST", { reason });
  const retry = (id, packageIds, targets, options) => send(`${BASE}/stage-requests/${query(id)}/retry`, "POST",
    { ...(Array.isArray(packageIds) && packageIds.length ? { packageIds } : {}), ...(Array.isArray(targets) && targets.length ? { targets } : {}) }, options);
  const retryPreparation = id => send(`${BASE}/stage-preparations/${query(id)}/retry`, "POST", {});
  // Hides a failed preparation notice for everyone; no request, Git or transport changes.
  const dismissPreparation = id => send(`${BASE}/stage-preparations/${query(id)}/dismiss`, "POST", {});
  const setProdLock = ({ packageId, locked, revision, reason } = {}) => send(`${BASE}/prod-lock`, "PUT",
    { packageId, locked, revision, reason, requestId: newRequestId() });
  // Archiv der letzten Stufe: nur Webapp, nie Git (archive.js).
  const archive = candidateIds => send(`${BASE}/stage-board/archive`, "POST", { candidateIds });
  const restoreArchived = candidateIds => send(`${BASE}/stage-board/unarchive`, "POST", { candidateIds });
  // DEV183: Karte „Aus der Pipeline entfernen“ (nur Webapp, nie Git oder Integration Suite) und Rückgängig.
  // candidateId der gezeigten Kartenzelle: passt sie nicht mehr zum Stand, antwortet der Server mit 409 STAGE_CARD_CHANGED.
  const removeCard = ({ packageId, stage, candidateId, loadedAt } = {}) => send(`${BASE}/stage-board/remove`, "POST",
    { packageId, stage, ...(candidateId ? { candidateId } : {}), ...(loadedAt ? { loadedAt } : {}) });
  const unremoveCards = removalIds => send(`${BASE}/stage-board/unremove`, "POST", { removalIds });
  // DEV185: Referenzhinweise für den Antragsdialog (nur lesend, nie sperrend); Antwort { referenceHints: { [packageId]: [...] } }.
  const loadReferenceHints = useCallback(async ({ packageIds, sourceStage, targetStage, targetIds, signal } = {}) => {
    if (!active) return null;
    const params = new URLSearchParams({ spaceId, packageIds: (packageIds || []).join(","), sourceStage: sourceStage || "", targetStage: targetStage || "" });
    if (Array.isArray(targetIds) && targetIds.length) params.set("targetIds", targetIds.join(","));
    return scopedCall(`${BASE}/stage-board/reference-hints?${params}`, { signal });
  }, [active, spaceId, scopedCall]);
  const loadRemovals = useCallback(async () => (active ? scopedCall(`${BASE}/stage-board/removals?spaceId=${query(spaceId)}`) : null), [active, spaceId, scopedCall]);
  // Gestellter Antrag mit Pin (Release-Ansicht) und „Auswahl aktualisieren“ (Vertrag 10.1, 14).
  const getRequest = async id => {
    if (!active) throw disabledError();
    const period = ownedScope.period;
    try { return await scopedCall(`${BASE}/stage-requests/${query(id)}?spaceId=${query(spaceId)}`); }
    catch (failure) { if (alive.current && scopeCurrent(period)) setActionError(asError(failure)); throw failure; }
  };
  const refreshRequest = (id, { packageIds, note } = {}) => send(`${BASE}/stage-requests/${query(id)}/refresh`, "POST",
    { requestId: newRequestId(), note, ...(Array.isArray(packageIds) && packageIds.length ? { packageIds } : {}) });
  const clearError = useCallback(() => setActionError(null), []);
  const loadDetails = useCallback(async (packageId, { force = false, sourceStage } = {}) => {
    if (!active || !packageId) return;
    const period = ownedScope.period, signature = packageDetailSignature(detailBoard.current, packageId, sourceStage);
    const old = detailCache.current.get(packageId);
    if (old?.signature === signature && (old.pending || !force && old.data)) return old.promise;
    const entry = { signature, sourceStage, pending: true, boardRevision: detailBoard.current?.readModel?.revision };
    detailCache.current.set(packageId, entry);
    setDetails(previous => ({ ...previous, [packageId]: { loading: true, data: null, error: null } }));
    const wanted = () => alive.current && scopeCurrent(period) && detailCache.current.get(packageId) === entry;
    entry.promise = (async () => {
      try {
        const suffix = sourceStage ? `&sourceStage=${query(sourceStage)}` : "";
        const path = `${BASE}/stage-board/package?spaceId=${query(spaceId)}&packageId=${query(packageId)}${suffix}`;
        const data = localReads ? await scopedCall(path, { timeoutMs: 10000 })
          : await readBoardPath(path, { request: scopedCall, stillWanted: wanted });
        if (!data || !Array.isArray(data.stages)) throw new Error("STAGE_DETAILS_INVALID");
        if (wanted()) { entry.data = data; setDetails(previous => ({ ...previous, [packageId]: { loading: false, data, error: null } })); }
      } catch (failure) {
        if (wanted()) setDetails(previous => ({ ...previous, [packageId]: { loading: false, data: null, error: asError(failure) } }));
      } finally { entry.pending = false; }
    })();
    return entry.promise;
  }, [active, spaceId, scopedCall, scopeCurrent, ownedScope, localReads]);
  // SSE-driven board changes invalidate only details of affected packages.
  // No interval and no new Git reads are introduced by this display refresh.
  useEffect(() => {
    if (!localReads || !state.board) return;
    for (const [packageId, entry] of detailCache.current) {
      if (!state.board.packages?.some(p => p.packageId === packageId)) {
        detailCache.current.delete(packageId);
        setDetails(previous => { const next = { ...previous }; delete next[packageId]; return next; });
      } else if (entry.signature !== packageDetailSignature(detailBoard.current, packageId, entry.sourceStage)
          || (!entry.data && !entry.pending && entry.boardRevision !== state.board.readModel?.revision)) {
        loadDetails(packageId, { sourceStage: entry.sourceStage });
      }
    }
  }, [state.board, localReads, loadDetails]);

  // Region nur im Mehrzielmodus und nur, wenn das Board sie kennt; „view“ ist das Board auf diese Region zugeschnitten.
  const currentScope = dataScope === scopeKey;
  const visibleBoard = useMemo(() => currentScope ? withRequestProgress(state.board, progress.requests, progress.complete) : null,
    [currentScope, state.board, progress.requests, progress.complete]);
  const activeRegion = visibleBoard?.multiTarget && region && regionsOf(visibleBoard).includes(region) ? region : null;
  const props = useMemo(() => (visibleBoard ? toStageBoardProps(visibleBoard, { includeDev, topicId, region: activeRegion }) : null),
    [visibleBoard, includeDev, topicId, activeRegion]);
  const view = useMemo(() => (visibleBoard ? projectRegion(visibleBoard, activeRegion) : null), [visibleBoard, activeRegion]);

  return { board: visibleBoard, boardRequestedAt: currentScope ? state.requestedAt : null, lastAcceptedAt: currentScope ? lastAcceptedAt : null,
    view, region: activeRegion, props, readModel: currentScope ? state.readModel : null, loading: currentScope ? state.loading : active,
    error: currentScope ? actionError || state.readError : null, readError: currentScope ? state.readError : null, actionError: currentScope ? actionError : null,
    refresh, refreshProgress, preparations: currentScope ? progress.preparations : [], progressError: currentScope ? progress.error : null,
    request, approve, reject, retry, retryPreparation, dismissPreparation, setProdLock, archive, restoreArchived,
    getRequest, refreshRequest, clearError, removeCard, unremoveCards, loadRemovals, loadReferenceHints,
    details: currentScope ? details : {}, loadDetails, uncertainActions: currentScope ? uncertainActions : [] };
}

export default useStageBoard;
